import {
    useCallback,
    useMemo,
} from 'react'

import useEnumValues from 'app/hooks/useEnumValues'
import useLabel from 'app/hooks/useLabel'
import SkyNetSpreadSheet, {
    getEditedItems,
    SkyNetSpreadSheetControlPanel,
} from 'app/shared-components/SkyNetSpreadSheet'
import {
    AsyncMutationOptions,
} from 'app/types/request.types'
import {
    FilterContextType,
    FilterOptions,
    FilterContext,
} from 'app/hooks/useFilter'

import useSyncState from 'app/hooks/useSyncState'
import requests from 'app/requests/RateCard.requests'
import {
    OtherLaneFeesTable,
} from '../OtherLaneFees.types'

import getConfig from './OtherLaneFeesSpreadsheet.config'
import feesFilterConfig from './OtherLaneFeesSpreadsheet.filterConfig'

export default function OtherLaneFeesSpreadsheet({
    filterOptions,
    onSave,
    onCancel,
}: Readonly<{
    filterOptions?: FilterOptions<OtherLaneFeesTable[]>,
    onSave?: (value: OtherLaneFeesTable[], options?: AsyncMutationOptions) => void,
    onCancel?: (...args: any[]) => void,
}>) {
    const getAllowedValues = useEnumValues()
    const getLabel = useLabel()
    const {
        value,
        setValue,
    } = useSyncState<OtherLaneFeesTable[]>(filterOptions?.filteredData)

    const editedItems = useMemo(() => {
        return getEditedItems({
            allItems: value,
            initData: filterOptions?.filteredData,
        })
    }, [
        filterOptions?.filteredData,
        value,
    ])

    const handleSave = useCallback(async (options?: { onSuccess?: (...a: any) => void }) => {
        return onSave(editedItems, options)
    }, [
        onSave,
        editedItems,
    ])

    const config = useMemo(() => {
        return getConfig({
            getLabel,
            getAllowedValues,
        })
    }, [
        getLabel,
        getAllowedValues,
    ])

    const edited = Boolean(editedItems.length)

    const saveBeforeFilter = useCallback((setFilter) => {
        handleSave({
            onSuccess: setFilter,
        })
    }, [handleSave])

    const filterConfig: FilterContextType = useMemo(() => {
        return {
            ...filterOptions,
            config: feesFilterConfig,
            request: requests.otherLaneFeesFilterCount,
            beforeFilterCallback: saveBeforeFilter,
            edited,
        }
    }, [
        edited,
        filterOptions,
        saveBeforeFilter,
    ])

    return (
        <FilterContext.Provider value={filterConfig}>
            <SkyNetSpreadSheetControlPanel
                onSave={handleSave}
                onCancel={onCancel}
                edited={edited}
            >
                <SkyNetSpreadSheet
                    config={config}
                    value={value}
                    disabled={false}
                    onChange={setValue}
                />
            </SkyNetSpreadSheetControlPanel>
        </FilterContext.Provider>
    )
}
